/**
 * NATSU Intelligence — legal document copy (Privacy Policy & Terms of Use).
 * Source-of-truth exact wording. Do not paraphrase.
 */

export const PRIVACY_POLICY = {
  title: "Privacy Policy",
  effectiveDate: "28 July 2026",
  sections: [
    {
      heading: "1. About This Privacy Policy",
      body: [
        "NATSU Intelligence respects the privacy of visitors, prospective investors, partners and organisations interested in its proposed platform and pilot programme.",
        "This Privacy Policy explains how information submitted through the NATSU Intelligence website is collected, used and protected.",
        "NATSU Intelligence is currently presented as an early-stage business initiative. The legal and corporate information in this Privacy Policy may be updated as the business structure develops.",
      ],
    },
    {
      heading: "2. Information We Collect",
      body: ["When you submit an enquiry, we may collect:"],
      list: [
        "your full name;",
        "your professional email address;",
        "your company or organisation;",
        "your professional role;",
        "your selected area of interest;",
        "information about a potential commercial challenge, pilot use case, investment enquiry or other business enquiry;",
        "your enquiry type;",
        "your consent status; and",
        "any later correspondence relating to your enquiry.",
      ],
      after: [
        "We may also process limited technical information required for website security, operation and performance, such as device, browser, access-time and security-log information.",
        "Please do not submit confidential documents, passwords, payment information, identity documents, health information or other sensitive personal information through the website form.",
      ],
    },
    {
      heading: "3. How We Use Information",
      body: ["We may use submitted information to:"],
      list: [
        "receive and review your enquiry;",
        "respond to you;",
        "assess whether a pilot, investment discussion, partnership or other business conversation may be appropriate;",
        "manage related correspondence;",
        "maintain an internal record of enquiries;",
        "protect the website against spam, fraud or misuse;",
        "operate and improve the website; and",
        "comply with applicable legal requirements.",
      ],
      after: [
        "Information submitted through the enquiry form will not automatically be used for unrelated marketing communications.",
      ],
    },
    {
      heading: "4. Consent",
      body: [
        "By selecting the consent checkbox and submitting the form, you consent to NATSU Intelligence using the submitted information to review and respond to your enquiry.",
        "You may withdraw your consent by contacting:",
        "moustakas@natsuintelligence.com",
        "Withdrawal will not affect processing carried out before the withdrawal.",
      ],
    },
    {
      heading: "5. How Enquiries Are Handled",
      body: ["Form submissions may be:"],
      list: [
        "delivered to the authorised NATSU Intelligence contact through a transactional-email provider;",
        "stored in a private Google Sheet or controlled business register;",
        "reviewed only by authorised persons supporting NATSU Intelligence; and",
        "used solely for legitimate website, enquiry and business-development purposes.",
      ],
      after: ["NATSU Intelligence does not sell or rent personal information."],
    },
    {
      heading: "6. Service Providers and International Processing",
      body: ["NATSU Intelligence may use service providers for:"],
      list: [
        "website hosting and development;",
        "form processing;",
        "transactional email;",
        "Google Workspace or comparable business-productivity services;",
        "website security; and",
        "privacy-conscious analytics where these are later enabled.",
      ],
      after: [
        "Some providers may process or store information outside the United Arab Emirates.",
        "NATSU Intelligence will seek to use reputable providers and reasonable safeguards appropriate to the nature of the information and the stage of the business.",
      ],
    },
    {
      heading: "7. Retention",
      body: [
        "Enquiry information will be retained only for as long as reasonably necessary to review and respond to the enquiry, maintain relevant business records or comply with applicable legal requirements.",
        "Inactive enquiries will ordinarily be reviewed for deletion or anonymisation within twenty-four months after the last meaningful communication.",
        "Information may be retained longer where:",
      ],
      list: [
        "an active discussion or commercial relationship continues;",
        "retention is required by law;",
        "it is necessary for security or record-keeping purposes; or",
        "it is needed to establish, exercise or defend legal rights.",
      ],
    },
    {
      heading: "8. Security",
      body: ["NATSU Intelligence uses reasonable technical and organisational measures intended to protect submitted information against unauthorised access, misuse, loss or disclosure.", "These measures may include:"],
      list: [
        "restricted access;",
        "secure credentials;",
        "protected environment variables;",
        "encrypted transmission where supported;",
        "access controls; and",
        "appropriate deletion and retention practices.",
      ],
      after: ["No method of electronic transmission or storage can be guaranteed to be completely secure."],
    },
    {
      heading: "9. Your Choices and Rights",
      body: ["Depending on applicable law, you may request:"],
      list: [
        "access to information held about you;",
        "correction of inaccurate information;",
        "deletion of information;",
        "restriction or cessation of certain processing;",
        "withdrawal of consent; or",
        "information about how your data is being used.",
      ],
      after: [
        "Requests may be sent to:",
        "moustakas@natsuintelligence.com",
        "We may request reasonable information to verify your identity before completing a request.",
      ],
    },
    {
      heading: "10. Cookies and Analytics",
      body: [
        "The website may use technologies that are strictly necessary for website functionality, security and form operation.",
        "Non-essential advertising or tracking cookies will not be activated without appropriate consent controls where legally required.",
      ],
    },
    {
      heading: "11. External Links",
      body: [
        "The website may contain links to third-party websites.",
        "NATSU Intelligence is not responsible for the privacy, security or content practices of external websites.",
      ],
    },
    {
      heading: "12. Children",
      body: [
        "The website is intended for professional and business users.",
        "NATSU Intelligence does not knowingly seek personal information from children through the enquiry form.",
      ],
    },
    {
      heading: "13. Updates",
      body: [
        "This Privacy Policy may be updated as the website, business structure, service providers or legal requirements develop.",
        "The effective date shown above will be updated when material changes are made.",
      ],
    },
    {
      heading: "14. Privacy Contact",
      body: [
        "Questions, requests or concerns may be directed to:",
        "NATSU Intelligence",
        "Email: moustakas@natsuintelligence.com",
      ],
    },
  ],
};

export const TERMS_OF_USE = {
  title: "Terms of Use",
  effectiveDate: "28 July 2026",
  sections: [
    {
      heading: "1. About These Terms",
      body: [
        "These Terms of Use apply to the NATSU Intelligence website.",
        "By using the website, you agree to use it in accordance with these Terms to the extent permitted by applicable law.",
        "NATSU Intelligence is currently presented as an early-stage business initiative. Legal and corporate details may be updated as the business structure develops.",
      ],
    },
    {
      heading: "2. Website Purpose",
      body: ["This website provides general information about:"],
      list: [
        "NATSU Intelligence;",
        "the proposed NATSU Commercial Intelligence Platform;",
        "the NATSU Commercial Knowledge Operating System™;",
        "planned product and pilot capabilities;",
        "prospective pilot engagements; and",
        "opportunities for investors, prospective customers and partners to contact NATSU Intelligence.",
      ],
      after: [
        "The website is intended for information, demonstration and business-development purposes.",
        "It is not a live maritime-operational system, navigation system, trading platform, legal-advisory service, financial-advisory service or automated decision authority.",
      ],
    },
    {
      heading: "3. Illustrative Product Content",
      body: [
        "Product interfaces, scenarios, financial figures, exposure ranges, recommendations, confidence levels, contract references, vessel information and commercial outcomes shown on the website may be illustrative.",
        "Unless expressly stated otherwise:",
      ],
      list: [
        "prototype interfaces use fictional sample data;",
        "figures are modelled examples;",
        "scenarios do not represent live customer or operational data;",
        "capabilities shown may represent planned platform or pilot direction;",
        "features may change during development and validation; and",
        "no displayed output should be treated as a real commercial recommendation.",
      ],
    },
    {
      heading: "4. No Professional Advice",
      body: [
        "Nothing on the website constitutes legal, financial, investment, tax, insurance, regulatory, sanctions, navigation, chartering or operational advice.",
        "Visitors remain responsible for obtaining appropriate professional advice and independently verifying information before making any commercial, contractual, operational, legal, regulatory, financial or investment decision.",
      ],
    },
    {
      heading: "5. Human Decision Responsibility",
      body: [
        "NATSU Intelligence is designed around governed human review and authorised decision-making.",
        "Any future product output must be reviewed against relevant evidence, contracts, assumptions, limitations, regulations and current circumstances.",
        "The authorised user or decision-maker remains responsible for approving, rejecting or acting upon any output.",
      ],
    },
    {
      heading: "6. Enquiries and Pilot Discussions",
      body: ["Submitting an enquiry does not:"],
      list: [
        "create a contract;",
        "guarantee acceptance into a pilot;",
        "create an advisory, partnership, employment or investment relationship;",
        "oblige NATSU Intelligence to provide services; or",
        "constitute an offer or commitment.",
      ],
      after: [
        "Any pilot, investment, partnership or commercial engagement will require separate review and, where appropriate, a separate written agreement.",
      ],
    },
    {
      heading: "7. Intellectual Property",
      body: [
        "The website and its original branding, text, graphics, interfaces, diagrams, product concepts, architecture, terminology and materials are owned by or licensed to NATSU Intelligence, except where otherwise stated.",
        "Certain names and identifiers may be proprietary or may be used as registered or unregistered trademarks.",
        "Access to the website does not transfer intellectual-property rights.",
        "Website content may not be reproduced, modified, distributed, commercially exploited or used to create derivative material without prior written permission.",
      ],
    },
    {
      heading: "8. Acceptable Use",
      body: ["You must not:"],
      list: [
        "use the website unlawfully or fraudulently;",
        "attempt unauthorised access;",
        "interfere with website operation or security;",
        "introduce malware or harmful material;",
        "conduct unauthorised automated scraping;",
        "submit false, misleading or abusive information;",
        "use the enquiry form for spam;",
        "impersonate another person or organisation; or",
        "misrepresent an affiliation with NATSU Intelligence.",
      ],
    },
    {
      heading: "9. Confidential Information",
      body: [
        "The public website and enquiry form are not secure channels for confidential, privileged or commercially sensitive materials.",
        "Do not submit:",
      ],
      list: [
        "trade secrets;",
        "complete contracts or charter parties;",
        "confidential customer information;",
        "identity documents;",
        "passwords;",
        "bank details;",
        "controlled technical information; or",
        "information you are not authorised to disclose.",
      ],
      after: [
        "Confidential information should be shared only after suitable safeguards and written arrangements are in place.",
      ],
    },
    {
      heading: "10. Accuracy and Availability",
      body: [
        "NATSU Intelligence seeks to present website information carefully but does not guarantee that all information is complete, current, uninterrupted or free from error.",
        "The website, content and planned product descriptions may be changed, restricted or withdrawn as the business and platform develop.",
      ],
    },
    {
      heading: "11. External Links",
      body: [
        "The website may contain links to third-party services or websites.",
        "NATSU Intelligence is not responsible for external content, availability, privacy or security practices.",
        "A third-party reference does not necessarily indicate affiliation, endorsement or partnership.",
      ],
    },
    {
      heading: "12. Liability",
      body: [
        "To the extent permitted by applicable law, NATSU Intelligence will not be responsible for indirect, incidental, consequential or economic loss arising from reliance on or inability to access the public website.",
        "The website is provided for information and demonstration purposes.",
        "Nothing in these Terms excludes any right or responsibility that cannot lawfully be excluded.",
      ],
    },
    {
      heading: "13. Privacy",
      body: ["Personal information submitted through the website is handled as described in the Privacy Policy."],
    },
    {
      heading: "14. Governing Framework",
      body: [
        "These Terms are intended to operate in accordance with applicable laws of the United Arab Emirates.",
        "The final governing-law and jurisdiction provisions may be updated when the operating legal entity and its registration jurisdiction are formally confirmed.",
      ],
    },
    {
      heading: "15. Changes to These Terms",
      body: [
        "These Terms may be updated as the website, product, business structure or applicable requirements develop.",
        "The effective date shown above will be updated when material changes are made.",
      ],
    },
    {
      heading: "16. Contact",
      body: [
        "Questions regarding these Terms may be directed to:",
        "NATSU Intelligence",
        "Email: moustakas@natsuintelligence.com",
      ],
    },
  ],
};
